import React, {FC, PropsWithChildren, useContext} from 'react';
import styled, {css} from 'styled-components';
import {Diff} from 'utility-types';

import {PredicateOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {
  CellsConversationsDragItem,
  CellsConversationsDragType,
} from '../../../../helpers/conversationCellHelpers';
import {LeftPanelContext} from '../../../app/leftPanelContext';
import {NavigationLine, NavigationLineProps} from '../../../common/navigation/navigationLine';
import {useWorkspaceDragAndDrop} from '../dnd/workspaceDragDropHooks';
import {WorkspaceDragItem, WorkspaceItemDropHandler} from '../dnd/workspaceDragDropTypes';
import {StyledWorkspaceLineMoreBadgeDiv} from './workspaceLineMore';

/*
 * Props.
 */

export interface WorkspaceLineDropTargetProps
  extends Diff<NavigationLineProps, WorkspaceLineDropTargetProvidedProps> {
  canConversationsDrop?: PredicateOf<CellsConversationsDragItem>;
  onConversationsDrop?: (item: CellsConversationsDragItem, retainTrackerAccess?: boolean) => void;
  workspaceDragItem: WorkspaceDragItem | undefined;
  onWorkspaceItemDrop?: WorkspaceItemDropHandler;
  shouldAlphabetizeWorkspace: boolean;
  /** Whether or not to show the badge (e.g. counter or edit mode checkbox) in the draggable reordering drag-and-drop preview. */
  shouldHidePreviewBadge?: boolean;
  isOpaque?: boolean;
}

const StyledWrapper = styled.div`
  position: relative;
  &::after {
    content: attr(data-id);
    position: absolute;
    font-size: 10px;
    left: 0;
    top: 0;
  }
`;

interface WorkspaceLineDropTargetStyleProps {
  $shouldHidePreviewBadge: boolean;
}

const StyledPreviewDiv = styled.div<WorkspaceLineDropTargetStyleProps>`
  position: absolute;
  z-index: -1;
  pointer-events: none;
  border-radius: 4px;

  ${(p) => addPreviewBadgeStyles(p)}
`;

function addPreviewBadgeStyles(p: WorkspaceLineDropTargetStyleProps) {
  if (p.$shouldHidePreviewBadge) {
    return css`
      ${StyledWorkspaceLineMoreBadgeDiv} {
        display: none;
      }
    `;
  }

  return '';
}

/*
 * Component.
 */

interface WorkspaceLineDropTargetProvidedProps {
  isDropping: boolean;
  didDrop: boolean;
  isOver: boolean;
  isRefusingDrop: boolean;
}

const isDebuggingDrop = false;

export const WorkspaceLineDropTarget: FC<PropsWithChildren<WorkspaceLineDropTargetProps>> = (props) => {
  const {scrollerElement} = useContext(LeftPanelContext);
  const {dndRef, dragPreviewRef, dropLinePreviewRef, collectedProps} = useWorkspaceDragAndDrop({
    scrollerElement,
    workspaceDragItem: props.workspaceDragItem,
    onWorkspaceItemDrop: props.onWorkspaceItemDrop,
    shouldAlphabetizeWorkspace: props.shouldAlphabetizeWorkspace,
    extra: {
      type: CellsConversationsDragType,
      canDrop: props.canConversationsDrop,
      onDrop: props.onConversationsDrop,
    },
  });

  return (
    <StyledWrapper ref={dndRef} data-id={(isDebuggingDrop && `${props.workspaceDragItem?.id}`) || ''}>
      <StyledPreviewDiv
        $shouldHidePreviewBadge={Boolean(props.shouldHidePreviewBadge)}
        ref={(el) =>
          dragPreviewRef(el, {
            offsetX: 1,
            offsetY: 1,
          })
        }
      >
        <NavigationLine
          isDropping={false}
          isDragging
          isSelected={false}
          hasHover={false}
          indentLevel={0}
          renderTooltip={() => null}
          disableBackground={false}
          isDisabled={false}
        >
          {props.children}
        </NavigationLine>
      </StyledPreviewDiv>
      <NavigationLine
        isDropping={collectedProps.isDropping}
        isSelected={props.isSelected}
        hasHover={props.hasHover}
        indentLevel={props.indentLevel}
        onClick={props.onClick}
        renderTooltip={props.renderTooltip}
        disableBackground={props.disableBackground}
        isDisabled={collectedProps.isRefusingDrop || props.isDisabled}
        isOpaque={props.isOpaque}
        interactionId={props.interactionId}
        to={props.to}
        previewRefCallback={dropLinePreviewRef}
        ignoreDisabledPointerStyling={props.ignoreDisabledPointerStyling}
      >
        {props.children}
      </NavigationLine>
    </StyledWrapper>
  );
};
